import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from './api';
import { Tache } from '../models/models';

interface TacheFilters {
  limit: number;
  offset: number;
}

interface TacheState {
  tacheData: Tache[];
  total: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  selectedTache: Tache | null;
  filters: TacheFilters;
}

const initialState: TacheState = {
  tacheData: [],
  total: 0,
  totalPages: 0,
  currentPage: 0,
  loading: false,
  error: null,
  successMessage: null,
  selectedTache: null,
  filters: {
    limit: 5,
    offset: 0,
  },
};

// Fetch all Taches with pagination
export const fetchTaches = createAsyncThunk<
  { data: Tache[]; total: number; totalPages: number; currentPage: number },
  TacheFilters,
  { rejectValue: string }
>('tache/fetchTaches', async (params, thunkAPI) => {
  try {
    const response = await api.get('/taches', { params });
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to load taches.');
  }
});

// Create or update a Tache
export const createTache = createAsyncThunk<Tache, Tache, { rejectValue: string }>(
  'tache/createOrUpdateTache',
  async (tacheData, thunkAPI) => {
    try {
      const response = await api.post('/taches/', tacheData);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to create or update.');
    }
  }
);

// Delete a Tache
export const deleteTache = createAsyncThunk<number, number, { rejectValue: string }>(
  'tache/deleteTache',
  async (tacheId, thunkAPI) => {
    try {
      await api.delete('/taches', { params: { tache_id: tacheId } });
      return tacheId;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Failed to delete.');
    }
  }
);

const tacheSlice = createSlice({
  name: 'tache',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setSelectedTache: (state, action: PayloadAction<Tache | null>) => {
      state.selectedTache = action.payload;
    },
    clearTacheData: (state) => {
      state.tacheData = [];
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
    setFilters: (state, action: PayloadAction<TacheFilters>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = {
        limit: 5,
        offset: 0,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTaches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTaches.fulfilled, (state, action) => {
        state.loading = false;
        state.tacheData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchTaches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createTache.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createTache.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Tache created/updated successfully!';
      })
      .addCase(createTache.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteTache.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteTache.fulfilled, (state, action: PayloadAction<number>) => {
        state.loading = false;
        state.successMessage = 'Tache deleted successfully!';
        state.tacheData = state.tacheData.filter(t => t.id !== action.payload);
      })
      .addCase(deleteTache.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setCurrentPage,
  setSelectedTache,
  clearTacheData,
  clearMessages,
  setFilters,
  resetFilters,
} = tacheSlice.actions;

export default tacheSlice.reducer;
